import { useRef } from "react";

import { useUnmount } from "app/custom-hooks/useUnmount";

type ResolveArg<T> = Parameters<typeof Promise.resolve<T>>[0];
type RejectArg = Parameters<typeof Promise.reject>[0];

type ResolveCb<T> = (value: ResolveArg<T>) => void;
type RejectCb = (reason?: RejectArg) => void;

interface IDeferredPromise<T> {
    promise: Promise<T>;
    resolve: ResolveCb<T>;
    reject: RejectCb;
}

function useDeferredPromise<T = void>() {
    const deferredRef = useRef<IDeferredPromise<T> | null>(null);

    const createPromise = (): Promise<T> => {
        if (deferredRef.current) {
            deferredRef.current.reject(new Error("New promise created"));
        }

        let resolve!: ResolveCb<T>;
        let reject!: RejectCb;

        const promise = new Promise<T>((res: ResolveCb<T>, rej: RejectCb) => {
            resolve = res;
            reject = rej;
        });

        deferredRef.current = { promise, resolve, reject };

        return promise;
    };

    const resolve = (value: T) => {
        if (!deferredRef.current) {
            console.error("The Deferred Promise was not created before resolve");

            return;
        }

        deferredRef.current.resolve(value);
        deferredRef.current = null;
    };

    const reject = (reason?: unknown) => {
        if (!deferredRef.current) {
            console.error("The Deferred Promise was not created before reject");

            return;
        }

        deferredRef.current.reject(reason);
        deferredRef.current = null;
    };

    useUnmount(() => {
        if (!deferredRef.current) {
            return;
        }

        deferredRef.current.reject(new Error("Component unmounted before deferred promise resolved"));

        deferredRef.current = null;
    });

    return {
        createPromise,
        resolve,
        reject,
    };
}

export default useDeferredPromise;
